import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs } from "../../../Styles";
import { ProgressBar, NewSelect, FeedCorrectionPopover } from "../..";
import { NewFieldStyles, NewFieldTypes } from "../../../Definitions";
import { useFeedcurveOptions, useSectionOptions } from "../../_Components";
import { AnimalInfo } from "../../../Services";
import { currentViewTypes, usePenContext } from "./penList";

/* eslint-disable */
enum ViewTypes {
  advanced = "advanced",
  simple = "simple"
}
/* eslint-enable */

const ANIMAL = ({ penIndex, animalIndex, defaultCursor }: {penIndex:number, animalIndex:number, defaultCursor:boolean}) => { 
  const { t } = useTranslation();
  const { feedcurveOptions } = useFeedcurveOptions();
  const { locationOptions } = useSectionOptions();

  const { state: { pens, tempData, currentViewType }, dispatch } = usePenContext()

  // #region | Animal
  const ThisAnimal = useMemo(() => {
    let AnimalData: AnimalInfo | undefined = undefined
    let pendData = pens.find(e => e.location_id === penIndex);
    if (pendData && pendData.animals) {
      AnimalData = pendData.animals.find(e => e.animal_id === animalIndex)
    }
    if (tempData && tempData.length) {
      let changes = tempData.find(e => (e.penId === penIndex || e.data.location === penIndex) && e.animalId === animalIndex)
      if (changes) {
        AnimalData = { ...AnimalData, ...changes.data } as AnimalInfo;
      }
    }
    return AnimalData
  }, [penIndex, animalIndex, pens, tempData])

  const animalInfo = useMemo(() => {
    // Feed Correction
    const feedCorrection = ThisAnimal
    ? ThisAnimal.correction === undefined || ThisAnimal.correction === null
      ? ""
      : ThisAnimal.correction === 0
        ? "0%"
        : `${ThisAnimal.correction}%`
    : "0%";
    
    // Milestone Day
    const milestoneDayNo = ThisAnimal?.milestone_day
    ? Math.floor(
        (new Date().getTime() - ThisAnimal.milestone_day) / (1000 * 60 * 60 * 24)
      )
    : "";

    // Milestone Name
    const milestoneName = ThisAnimal?.milestone ? ThisAnimal.milestone.name : "";

    const feedCurveName = ThisAnimal?.feedCurve ? ThisAnimal.feedCurve.name : "";
    
    return {
      feedCurveName,
      feedCorrection,
      milestone:{dayNo:milestoneDayNo, name:milestoneName},
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ThisAnimal])
  // #endregion
  
  // #region | Progressbar
  const ConsumptionProgressBar: React.FC<{ ifHasExtra?: boolean }> = ({ifHasExtra}) => {
    const const_Today = ThisAnimal?.consumptions?.find(
      e => e.milestoneDayOffset !== undefined && e.milestoneDayOffset === 0
    );
  
    if (!const_Today) return <></>;
  
    const progressBarTotal = (const_Today.total ?? 0) / 1000;
    const progressBarProgressed = (const_Today.progressed ?? 0) / 1000;
  
    return (
      <ProgressBar
        extension={{ extensionOnly: ifHasExtra }}
        total={+(progressBarTotal).toFixed(2)}
        progressed={+(progressBarTotal - progressBarProgressed).toFixed(2)}
      />
    );
  };

  const ProgressWithExtra = useMemo(() => (
    <ConsumptionProgressBar ifHasExtra={true} />
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [ThisAnimal]);
  
  const ProgressWithoutExtra = useMemo(() => (
    <ConsumptionProgressBar ifHasExtra={false} />
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [ThisAnimal]);
  // #endregion

  const textStyle = {...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, cursor: "pointer"}
  
  if (ThisAnimal === undefined) return <>DATA ERROR</>
  switch (currentViewType) {
    case currentViewTypes.basic:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 108px 45px 108px 45px 90px 50px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px", cursor: "pointer" }}>
          <div style={{...textStyle}}>{ThisAnimal.animal_number}</div>
          <div style={{...textStyle}}>{animalInfo.milestone.name}</div>
          <div style={{...textStyle}}>{animalInfo.milestone.dayNo}</div>
          <div style={{...textStyle, paddingLeft: "5px"}}>{animalInfo.feedCurveName}</div>
          <div style={{...textStyle, paddingLeft: "5px"}}>{animalInfo.feedCorrection}</div>
          <div style={{...textStyle}}>{ProgressWithoutExtra}</div>
          <div style={{...textStyle}}>{ProgressWithExtra}</div>
        </div>
      )
    case currentViewTypes.edit:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 108px 45px 108px 45px 90px 50px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px" }}>
          <div>{ThisAnimal.animal_number}</div>
          <div>{animalInfo.milestone.name}</div>
          <div>{animalInfo.milestone.dayNo}</div>
          <NewSelect
            onChange={(selectedValue) => dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { feedcurve_id: Number(selectedValue?.id)} }] })}
            options={feedcurveOptions} 
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary, 
              fieldType: NewFieldTypes.tertiaryField, 
              fieldWidth: "100%",
              menuWidth: "280px"
            }}  
            valueOverrides={{
              defaultValue: feedcurveOptions.find(f => f.id === ThisAnimal.feedCurve?.feedCurveId)
            }}
          />
          <FeedCorrectionPopover
            onChange={(selectedValue) => dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { correction: selectedValue } }] })}
            defaultValue={ThisAnimal.correction}
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary,
              fieldType: NewFieldTypes.tertiaryField,
              fieldWidth: "100%"
            }}
          />
          <div>{ProgressWithoutExtra}</div>
          <div>{ProgressWithExtra}</div>
        </div>
      )
    case currentViewTypes.add:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 108px 45px 108px 45px 90px 50px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px", cursor: "default", zIndex: "9999" }}>
          <div>{ThisAnimal.animal_number}</div>
          <div>{animalInfo.milestone.name}</div>
          <div>{animalInfo.milestone.dayNo}</div>
          <div>{animalInfo.feedCurveName}</div>
          <div>{animalInfo.feedCorrection}</div>
          <div>{ProgressWithoutExtra}</div>
          <div>{ProgressWithExtra}</div>
        </div>
      )
    case currentViewTypes.move:
      return (
        <div style={{ display: "grid", gridTemplateColumns: "45px 20px 55px 108px 223px", height: "35px", textAlign: "start", alignItems: "center", gap: "5px", cursor: "default", zIndex: "9999" }}>
          <div>{ThisAnimal.animal_number}</div>
          <glyphs.Arrow fillColor={colors.greyDarkPrimary} />
          <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary }}>{t("params.location", {defaultValue: "Location"})}</p>
          <NewSelect
            onChange={(selectedValue) => dispatch({ tempData: [{ penId: penIndex, animalId: animalIndex, data: { location: Number(selectedValue?.id)} }] })} // TODO
            options={locationOptions} 
            fieldOverrides={{
              fieldStyle: NewFieldStyles.greyTertiary, 
              fieldType: NewFieldTypes.tertiaryField, 
              fieldWidth: "100%",
              menuWidth: "280px"
            }}  
            valueOverrides={{
              defaultValue: locationOptions.find(l => l.id === ThisAnimal.location_id)
            }}
          />
        </div>
      )
    default:
      return <>View STATE ERROR</>
  }
}

export default ANIMAL